import { Stack } from '@mui/material';
import {
  Assessment,
  AssessmentEntry,
  AssessmentGroupWithStudents,
  AssessmentMethod,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentsGradeOption,
  AssessmentWithEntries,
  FilterKeys,
  GetMarkbookSort,
  GetMarkbookSortBy,
  isAssessmentMethodGrade,
  isAssessmentMethodScore,
  isGetMarkbookSortWithParams,
} from '@schooly/api';
import {
  CellParams,
  ColumnDef,
  commonCellProps,
  createCellPropsFactory,
} from '@schooly/components/data-grid';
import React, { ReactNode } from 'react';

import { ProfileModalMode } from '../../../../context/profile/helpers';
import { HandleChangeSortParams } from '../MarkbookPreview/useAssessmentsMarkbookForGroups';
import { MarkbookColumnArrangeBy } from '../MarkbookSettings/MarkbookSettings';
import {
  getMarkbookForGroupAssessmentParams,
  getMarkbookForGroupsAssessmentEntries,
  isMarkbookForGroupEntryCellEmpty,
} from './assessmentEntryUpdates';
import {
  createMarkbookForGroupFieldKey,
  createMarkbookForGroupsAverageFieldKey,
  createMarkbookForGroupsMethodFieldKey,
} from './assessmentFieldKeys';
import { getAssessmentEntriesDataFromMarkbookEntries } from './assessmentMethodRendering';
import { MarkbookForGroupsCellEditableProps } from './assessmentPermissions';
import {
  ASSESSMENT_COL_WIDTH,
  ASSESSMENT_CSS_CLASSES,
  ASSESSMENT_ROW_HEIGHT,
  BASELINE_COL_FIELD_KEY,
  getAssessmentSubColWidth,
  STUDENT_COL_FIELD_KEY,
  useMarkbookForGroupsStyling,
  useMarkbookForGroupStyling,
} from './assessmentStyling';
import { CellColorProps } from './colouredCells';
import { GradeUpdateParams } from './handlers/gradeHandlers';
import { ScoreUpdateParams } from './handlers/scoreHandlers';
import {
  AssessmentMarkbookDisabledMethodTypeCell,
  AssessmentMarkbookEmptyCell,
  MarkbookAverageCategoryCell,
  MarkbookCategoryNameCell,
  MarkbookForGroupStudentsHeaderCell,
  MarkbookLoadMoreCell,
  MarkbookMethodTypeCell,
  MarkbookMultipleGroupsEntriesCell,
  MarkbookStudentNameCell,
  MarkbookStudentNameSkeletonCell,
} from './MarkbookCell';
import { AverageCellRenderer } from './renderers/AverageCellRenderer';
import { GradeCellRenderer } from './renderers/GradeCellRenderer';
import { ScoreCellRenderer } from './renderers/ScoreCellRenderer';
import {
  AssessmentCellState,
  isMarkbookForGroupsStudentRow,
  MarkbookBaseline,
  MarkbookBaselineConfig,
  MarkbookColumnType,
  MarkbookForGroupRow,
  MarkbookForGroupsCategoryRow,
  MarkbookForGroupsColumn,
  MarkbookForGroupsColumnData,
  MarkbookForGroupsRow,
  MarkbookForGroupsRowType,
  MarkbookSubject,
  RenderMarkbookForGroupMethodProps,
  RenderMarkbookForGroupsMethodProps,
} from './types';
import {
  getMarkbookSubject,
  getMarkbookSubjects,
  getMethodSortBy,
  hasMultipleGroupsEntriesInCell,
  MARKBOOK_OTHER_SUBJECT_ID,
  MARKBOOK_TUTOR_GROUP,
  sortByGroupName,
} from './utils';

type BaseColumnData = {
  height: number;
  field: string;
  sortable: boolean;
  disableColumnMenu: boolean;
  width: number;
  editable: boolean;
  renderHeader: () => null;
};

type RenderMethodCellParams = {
  subjectId: string;
  isDisabled?: boolean;
  method: AssessmentMethod;
  studentRelationId: string;
  entries: AssessmentEntry[];
  groupId: string;
  assessmentId: string;
  canEdit: boolean;
  scoreUpdateHandler: (params: ScoreUpdateParams) => Promise<void>;
  gradeUpdateHandler: (params: GradeUpdateParams) => Promise<void>;
  options: AssessmentsGradeOption[];
  scoreControllerProps?: Record<string, any>;
  gradeControllerProps?: Record<string, any>;
  params: CellParams<MarkbookForGroupRow | MarkbookForGroupsRow, AssessmentCellState>;
  categoryId?: string;
  renderFallback?: () => ReactNode;
};

const COMPARABLE_METHOD_TYPES = [AssessmentMethodType.Score, AssessmentMethodType.Grade];

const renderMethodCell = ({
  method,
  isDisabled,
  studentRelationId,
  entries,
  groupId,
  assessmentId,
  subjectId,
  canEdit,
  scoreUpdateHandler,
  gradeUpdateHandler,
  options,
  scoreControllerProps,
  gradeControllerProps,
  params,
  categoryId,
  renderFallback,
}: RenderMethodCellParams): ReactNode => {
  const key = `${studentRelationId}-${groupId}-${method.id}-${subjectId}`;
  if (isAssessmentMethodScore(method)) {
    return (
      <ScoreCellRenderer
        key={`score-${key}`}
        subjectId={subjectId}
        isDisabled={isDisabled}
        studentRelationId={studentRelationId}
        method={method}
        entries={entries}
        canEdit={canEdit}
        groupId={groupId}
        assessmentId={assessmentId}
        onScoreUpdate={scoreUpdateHandler}
        scoreControllerProps={scoreControllerProps}
        categoryId={categoryId}
        {...params}
      />
    );
  }

  if (isAssessmentMethodGrade(method)) {
    return (
      <GradeCellRenderer
        key={`grade-${key}`}
        subjectId={subjectId}
        isDisabled={isDisabled}
        studentRelationId={studentRelationId}
        method={method}
        entries={entries}
        groupId={groupId}
        assessmentId={assessmentId}
        onGradeUpdate={gradeUpdateHandler}
        canEdit={canEdit}
        options={options}
        gradeControllerProps={gradeControllerProps}
        categoryId={categoryId}
        {...params}
      />
    );
  }

  return renderFallback?.();
};

const createAssessmentCellProps = (
  getAssessmentEntryColor?: (cellProps: CellColorProps) => string | null,
) =>
  createCellPropsFactory({
    score: {
      common: { ...commonCellProps.tableView },
      view: { focused: false, getCellColor: getAssessmentEntryColor },
      edit: { focused: true },
    },
    grade: {
      common: { ...commonCellProps.focusStop, ...commonCellProps.tableView },
      view: { focused: false, getCellColor: getAssessmentEntryColor },
      edit: {
        // TODO Remove focused prop when commentController will be removed, cell should be focused by standard behavior
        focused: true,
        ...commonCellProps.editMode,
        noStopPropagationOnSelect: true,
        disableSpaceKeyPress: true,
        showHorizontalBorder: true,
        canReset: true,
      },
    },
    comment: {
      common: { ...commonCellProps.focusStop, ...commonCellProps.tableView },
      view: {},
      edit: {
        open: true,
        autoFocusOnEdit: true,
        ...commonCellProps.editMode,
        saveOnEnterPress: true,
      },
    },
  });

//TODO TR-9518 Temporary cell props to remove hover effect
const createMarkbookForGroupsAssessmentCellProps = () =>
  createCellPropsFactory({
    score: {
      common: { ...commonCellProps.tableView },
      view: { focused: false, hover: false },
      edit: { focused: true, hover: false },
    },
    grade: {
      common: { ...commonCellProps.focusStop, ...commonCellProps.tableView },
      view: {
        focused: false,
        hover: false,
      },
      edit: {
        focused: true,
        ...commonCellProps.editMode,
        noStopPropagationOnSelect: true,
        hover: false,
        showHorizontalBorder: true,
        canReset: true,
      },
    },
    comment: {
      common: { ...commonCellProps.focusStop, ...commonCellProps.tableView },
      view: { hover: false },
      edit: {
        open: true,
        autoFocusOnEdit: true,
        ...commonCellProps.editMode,
        saveOnEnterPress: true,
        hover: false,
      },
    },
  });

type CreateMarkbookForGroupColumnsParams = {
  assessments: AssessmentWithEntries[];
  renderAssessmentEntryMethod?: (props: RenderMarkbookForGroupMethodProps) => React.ReactNode;
  styling?: ReturnType<typeof useMarkbookForGroupStyling>;
  scoreUpdateHandler: (params: ScoreUpdateParams) => Promise<void>;
  permissionChecker: { isCellEditable: (studentId: string, field: string) => boolean };
  // TODO create single update handler when all methods will be migrated to use middleware
  gradeUpdateHandler: (params: GradeUpdateParams) => Promise<void>;
  lists: Record<string, AssessmentsGrade>;
};

export const createMarkbookForGroupColumns = ({
  assessments,
  renderAssessmentEntryMethod,
  styling,
  permissionChecker,
  scoreUpdateHandler,
  gradeUpdateHandler,
  lists,
}: CreateMarkbookForGroupColumnsParams): ColumnDef<
  MarkbookForGroupRow,
  AssessmentCellState,
  undefined
>[] => {
  const cellProps = createAssessmentCellProps();

  const assessmentColumns = [
    ...assessments.flatMap((assessment, assessmentIndex) =>
      assessment.methods.map((method, methodIndex) => {
        const baseColumn: BaseColumnData = {
          field: createMarkbookForGroupFieldKey({
            assessmentId: assessment.id,
            methodType: method.method_type,
            assessmentIndex,
            methodIndex,
          }),
          sortable: false,
          disableColumnMenu: true,
          width: getAssessmentSubColWidth(assessment.methods.length),
          editable: true,
          height: ASSESSMENT_ROW_HEIGHT,
          renderHeader: () => null,
        };

        const group: AssessmentGroupWithStudents | undefined = assessment.groups[0];
        const subjectId = group ? getMarkbookSubject(group).id : MARKBOOK_OTHER_SUBJECT_ID;

        return createMarkbookForGroupMethodColumn({
          baseColumn,
          method,
          subjectId,
          assessmentEntries: assessment,
          cellProps,
          scoreUpdateHandler,
          permissionChecker,
          gradeUpdateHandler,
          lists,
          renderAssessmentEntryMethod,
        });
      }),
    ),
  ];

  return [createMarkbookForGroupStudentColumn(styling), ...assessmentColumns];
};

const createMarkbookForGroupMethodColumn = ({
  subjectId,
  baseColumn,
  method,
  assessmentEntries,
  cellProps,
  scoreUpdateHandler,
  permissionChecker,
  gradeUpdateHandler,
  lists,
  renderAssessmentEntryMethod,
}: {
  baseColumn: BaseColumnData;
  subjectId: string;
  method: AssessmentMethod;
  assessmentEntries: AssessmentWithEntries;
  cellProps: ReturnType<typeof createCellPropsFactory>;
  scoreUpdateHandler: (params: ScoreUpdateParams) => Promise<void>;
  permissionChecker: { isCellEditable: (studentId: string, field: string) => boolean };
  gradeUpdateHandler: (params: GradeUpdateParams) => Promise<void>;
  lists: Record<string, AssessmentsGrade>;
  renderAssessmentEntryMethod?: (props: RenderMarkbookForGroupMethodProps) => React.ReactNode;
}): ColumnDef<MarkbookForGroupRow, AssessmentCellState, undefined> => {
  const methodConfig = getMethodConfig({
    cellProps,
    baseColumn,
  });

  const config = methodConfig[method.method_type];
  const className =
    cellClassNameConfig[MarkbookColumnType.Assessment][MarkbookForGroupsRowType.Student][
      method.method_type
    ];

  return {
    field: baseColumn.field,
    sortable: baseColumn.sortable,
    disableColumnMenu: baseColumn.disableColumnMenu,
    width: baseColumn.width,
    editable: baseColumn.editable,
    headerClassName: ASSESSMENT_CSS_CLASSES.methodTypeCell,
    renderHeader: () => null,
    cellClassName: className,
    align: config.align as 'left' | 'center' | 'right',
    renderCell: (params: CellParams<MarkbookForGroupRow, AssessmentCellState>) => {
      const { row, field } = params;

      const student = row.student;
      const isEmpty = isMarkbookForGroupEntryCellEmpty(
        student.user?.relation_id || '',
        method,
        assessmentEntries,
      );

      const cellId = `${row.id}_${field}`;

      if (isEmpty) {
        return <AssessmentMarkbookEmptyCell />;
      }

      const entries = getMarkbookForGroupAssessmentParams(
        student.user.relation_id,
        method,
        assessmentEntries,
      );

      const group: AssessmentGroupWithStudents | undefined = assessmentEntries.groups[0];

      return (
        <Stack {...config.stackProps}>
          {renderMethodCell({
            method,
            isDisabled: false,
            subjectId,
            studentRelationId: student.user.relation_id,
            entries: entries?.entries ?? [],
            groupId: group?.id ?? '',
            assessmentId: assessmentEntries.id,
            canEdit: permissionChecker.isCellEditable(student.user.relation_id, field),
            scoreUpdateHandler,
            gradeUpdateHandler,
            options: lists[method.select_list_id ?? '']?.options ?? [],
            ...config.viewProps,
            params,
            renderFallback: () =>
              renderAssessmentEntryMethod?.({
                student,
                method,
                assessmentEntries,
                cellId,
                ...config.viewProps,
              }),
          })}
        </Stack>
      );
    },
    renderEditCell: (params: CellParams<MarkbookForGroupRow, AssessmentCellState>) => {
      const { row, field } = params;
      const student = row.student;
      const editStackProps =
        method.method_type === AssessmentMethodType.Comment
          ? { minWidth: baseColumn.width, height: '100%' }
          : config.stackProps;

      const cellId = `${row.id}_${field}`;

      const entries = getMarkbookForGroupAssessmentParams(
        student.user.relation_id,
        method,
        assessmentEntries,
      );

      const group: AssessmentGroupWithStudents | undefined = assessmentEntries.groups[0];

      return (
        <Stack {...editStackProps}>
          {renderMethodCell({
            method,
            studentRelationId: student.user.relation_id,
            entries: entries?.entries ?? [],
            groupId: group?.id ?? '',
            assessmentId: assessmentEntries.id,
            canEdit: permissionChecker.isCellEditable(student.user.relation_id, field),
            subjectId,
            scoreUpdateHandler,
            gradeUpdateHandler,
            options: lists[method.select_list_id ?? '']?.options ?? [],
            ...config.editProps,
            params,
            renderFallback: () =>
              renderAssessmentEntryMethod?.({
                student,
                method,
                assessmentEntries,
                cellId,
                ...config.editProps,
              }),
          })}
        </Stack>
      );
    },
  };
};

export const createMarkbookForGroupStudentColumn = (
  styling?: ReturnType<typeof useMarkbookForGroupStyling>,
): ColumnDef<MarkbookForGroupRow, AssessmentCellState, undefined> => ({
  field: STUDENT_COL_FIELD_KEY,
  sortable: false,
  disableColumnMenu: true,
  flex: 1,
  minWidth: styling?.studentNameCellWidth,
  cellClassName: cellClassNameConfig.students[MarkbookForGroupsRowType.Student],
  renderHeader: () => <MarkbookForGroupStudentsHeaderCell />,
  renderCell: (params: CellParams<MarkbookForGroupRow, AssessmentCellState>) => {
    const { row } = params;
    const student = row.student;
    return (
      <MarkbookStudentNameCell
        student={student.user}
        cellProps={{ pl: 1.5, sx: styling?.studentCellSx }}
      />
    );
  },
});

type CreateMarkbookForGroupsColumnsParams = {
  assessments: Assessment[];
  scoreUpdateHandler: (params: ScoreUpdateParams) => Promise<void>;
  permissionChecker: { isCellEditable: (params: MarkbookForGroupsCellEditableProps) => boolean };
  renderEntryCell: (props: RenderMarkbookForGroupsMethodProps) => React.ReactNode;
  styling?: ReturnType<typeof useMarkbookForGroupsStyling>;
  isFetchingNextPage: (id: string) => boolean;
  fetchNextPage: (id: string) => void;
  onChangeSort: (params: HandleChangeSortParams) => void;
  sort?: GetMarkbookSort;
  isSorting: boolean;
  gradeUpdateHandler: (params: GradeUpdateParams) => Promise<void>;
  lists: Record<string, AssessmentsGrade>;
  onCellClickOverride?: (e: React.MouseEvent) => void;
  onSetBaselineConfig?: (v: MarkbookBaselineConfig | null) => void;
  columnArrangeBy: MarkbookColumnArrangeBy;
  baseline?: MarkbookBaseline;
  showStudentsAverage?: boolean;
};

export const createMarkbookForGroupsColumns = ({
  assessments,
  scoreUpdateHandler,
  permissionChecker,
  renderEntryCell,
  styling,
  isFetchingNextPage,
  fetchNextPage,
  onChangeSort,
  sort,
  isSorting,
  gradeUpdateHandler,
  lists,
  onCellClickOverride,
  onSetBaselineConfig,
  columnArrangeBy,
  baseline,
  showStudentsAverage,
}: CreateMarkbookForGroupsColumnsParams): MarkbookForGroupsColumn[] => {
  const cellProps = createMarkbookForGroupsAssessmentCellProps();

  const createAssessmentColumnForSubject = ({
    subject,
    assessment,
    disabledMethodId,
  }: {
    subject: MarkbookSubject & {};
    assessment: Assessment;
    disabledMethodId?: string;
  }) => {
    return assessment.methods.map((method) => {
      const baseColumn: BaseColumnData = {
        field: createMarkbookForGroupsMethodFieldKey({
          assessmentId: assessment.id,
          subjectId: subject.id,
          methodType: method.method_type,
        }),
        sortable: false,
        disableColumnMenu: true,
        width: getAssessmentSubColWidth(assessment.methods.length),
        editable: true,
        renderHeader: () => null,
        height: ASSESSMENT_ROW_HEIGHT,
      };
      const isDisabled = disabledMethodId === method.id;
      const isComparable =
        !!onSetBaselineConfig && COMPARABLE_METHOD_TYPES.includes(method.method_type);

      return createMarkbookForGroupsMethodColumn({
        baseColumn,
        method,
        assessment,
        subject,
        isDisabled,
        onToggleCompare: isComparable
          ? () => {
              onSetBaselineConfig?.({
                kind: 'assessment',
                assessmentId: assessment.id,
                method,
                subject,
              });
            }
          : undefined,
        scoreUpdateHandler,
        renderEntryCell,
        sort,
        onChangeSort,
        isSorting,
        cellProps,
        permissionChecker,
        gradeUpdateHandler,
        onCellClickOverride,
        list: lists[method.select_list_id ?? ''] ?? [],
      });
    });
  };

  const createAverageColumnForSubject = ({
    baseColumn,
    subject,
    isDisabled,
    isCompared,
    onToggleCompare,
  }: {
    baseColumn: BaseColumnData;
    subject: MarkbookSubject;
    isDisabled?: boolean;
    isCompared?: boolean;
    onToggleCompare: () => void;
  }) => {
    const columnData = {
      data: { subject },
      type: 'average' as const,
      field: baseColumn.field,
      computedWidth: baseColumn.width,
    };

    return {
      field: baseColumn.field,
      sortable: baseColumn.sortable,
      disableColumnMenu: baseColumn.disableColumnMenu,
      width: baseColumn.width,
      editable: false,
      data: columnData.data,
      renderHeader: baseColumn.renderHeader,
      cellClassName: ({ row }: { row: MarkbookForGroupsRow }) => {
        return cellClassNameConfig[MarkbookColumnType.Average][row.type];
      },
      renderCell: (params: CellParams<MarkbookForGroupsRow, AssessmentCellState>) => {
        const renderCellContent = () => {
          switch (params.row.type) {
            case MarkbookForGroupsRowType.Student: {
              const { row } = params;
              const { average } = row.data;
              const averageForSubject = average.find((i) => {
                const isTutorSubject = subject.id === MARKBOOK_TUTOR_GROUP.id;
                return isTutorSubject ? i.subject_id === null : i.subject_id === subject.id;
              });
              if (!averageForSubject) {
                return <AssessmentMarkbookEmptyCell />;
              }

              return (
                <AverageCellRenderer
                  isDisabled={isDisabled}
                  groupId={row.groupId}
                  subjectId={subject.id}
                  average={averageForSubject}
                  studentRelationId={row.data.student.relation_id}
                  {...params}
                />
              );
            }
            case MarkbookForGroupsRowType.Category: {
              return (
                <MarkbookAverageCategoryCell
                  isCompared={isCompared}
                  isDisabled={isDisabled}
                  onToggleCompare={onToggleCompare}
                />
              );
            }
          }
        };

        return renderCellContent();
      },
    };
  };

  const createBaselineAssessmentColumnForSubject = (baseline: MarkbookBaseline) => {
    const baseColumn: BaseColumnData = {
      field: BASELINE_COL_FIELD_KEY,
      sortable: false,
      disableColumnMenu: true,
      width: ASSESSMENT_COL_WIDTH,
      editable: false,
      renderHeader: () => null,
      height: ASSESSMENT_ROW_HEIGHT,
    };

    switch (baseline.kind) {
      case 'assessment': {
        const { subject, assessment, method } = baseline;
        const subjectId = subject.id === MARKBOOK_TUTOR_GROUP.id ? null : subject.id;
        return createMarkbookForGroupsMethodColumn({
          baseColumn,
          method,
          assessment,
          subject: {
            id: subject.id,
            groups: assessment.groups.filter((g) => g.subject?.id === subjectId),
          },
          scoreUpdateHandler,
          renderEntryCell,
          sort,
          onChangeSort,
          isCompared: true,
          onToggleCompare: () => onSetBaselineConfig?.(null),
          isSorting,
          cellProps,
          permissionChecker: { isCellEditable: () => false },
          gradeUpdateHandler,
          list: lists[method?.select_list_id ?? ''] ?? [],
        });
      }
      case 'average': {
        const { subject } = baseline;

        const baseColumn: BaseColumnData = {
          field: BASELINE_COL_FIELD_KEY,
          sortable: false,
          disableColumnMenu: true,
          width: ASSESSMENT_COL_WIDTH,
          editable: true,
          renderHeader: () => null,
          height: ASSESSMENT_ROW_HEIGHT,
        };

        return createAverageColumnForSubject({
          baseColumn,
          isCompared: true,
          onToggleCompare: () => onSetBaselineConfig?.(null),
          subject,
        });
      }
      default:
        const _: never = baseline;
        return _;
    }
  };

  let assessmentColumns: MarkbookForGroupsColumn[] = [];

  switch (columnArrangeBy) {
    case FilterKeys.Subject: {
      if (baseline) {
        assessmentColumns.push(createBaselineAssessmentColumnForSubject(baseline));
      }

      const subjects = getMarkbookSubjects(assessments.flatMap((assessment) => assessment.groups));

      for (const subject of subjects) {
        const subjectGroupsIds = subject.groups.map(({ id }) => id);

        const subjectAssessments = assessments.filter((assessment) =>
          assessment.groups.some(({ id }) => subjectGroupsIds.includes(id)),
        );

        for (const assessment of subjectAssessments) {
          const assessmentDisabled =
            baseline?.kind === 'assessment' &&
            baseline?.assessment.id === assessment.id &&
            baseline?.subject.id === subject.id;

          const disabledMethodId = assessmentDisabled ? baseline.method.id : undefined;
          const columns = createAssessmentColumnForSubject({
            subject,
            assessment,
            disabledMethodId,
          });
          assessmentColumns.push(...columns);
        }

        const isCompared = baseline?.kind === 'average' && baseline?.subject.id === subject.id;

        if (showStudentsAverage) {
          assessmentColumns.push(
            createAverageColumnForSubject({
              baseColumn: {
                field: createMarkbookForGroupsAverageFieldKey({
                  subjectId: subject.id,
                }),
                sortable: false,
                disableColumnMenu: true,
                width: ASSESSMENT_COL_WIDTH,
                editable: true,
                renderHeader: () => null,
                height: ASSESSMENT_ROW_HEIGHT,
              },
              isCompared,
              onToggleCompare: () =>
                onSetBaselineConfig?.(isCompared ? null : { kind: 'average', subject }),
              subject,
              isDisabled: baseline?.kind === 'average' && baseline?.subject.id === subject.id,
            }),
          );
        }
      }

      break;
    }
    case FilterKeys.Assessment: {
      assessmentColumns = assessments.flatMap((assessment) => {
        const subjects = getMarkbookSubjects(assessment.groups);
        return subjects.flatMap((subject) =>
          createAssessmentColumnForSubject({ subject, assessment }),
        );
      });
      break;
    }
    default: {
      const _: never = columnArrangeBy;
      return _;
    }
  }

  return [
    createMarkbookForGroupsStudentColumn({
      styling,
      fetchNextPage,
      isFetchingNextPage,
      onChangeSort,
      sort,
      isSorting,
    }),
    ...assessmentColumns,
  ];
};

type CreateMarkbookForGroupsMethodColumnParams = {
  baseColumn: BaseColumnData;
  method: AssessmentMethod;
  assessment: Assessment;
  subject: MarkbookSubject;
  scoreUpdateHandler: (params: ScoreUpdateParams) => Promise<void>;
  permissionChecker: { isCellEditable: (params: MarkbookForGroupsCellEditableProps) => boolean };
  renderEntryCell: (props: RenderMarkbookForGroupsMethodProps) => React.ReactNode;
  sort?: GetMarkbookSort;
  onChangeSort: (params: HandleChangeSortParams) => void;
  isSorting: boolean;
  isDisabled?: boolean;
  isCompared?: boolean;
  cellProps: ReturnType<typeof createCellPropsFactory>;
  gradeUpdateHandler: (params: GradeUpdateParams) => Promise<void>;
  list: AssessmentsGrade;
  onCellClickOverride?: (e: React.MouseEvent) => void;
  onToggleCompare?: () => void;
};

const createMarkbookForGroupsMethodColumn = ({
  baseColumn,
  method,
  assessment,
  subject,
  scoreUpdateHandler,
  gradeUpdateHandler,
  list,
  permissionChecker,
  renderEntryCell,
  sort,
  onCellClickOverride,
  onToggleCompare,
  onChangeSort,
  isSorting,
  isDisabled,
  isCompared,
  cellProps,
}: CreateMarkbookForGroupsMethodColumnParams): ColumnDef<
  MarkbookForGroupsRow,
  AssessmentCellState,
  MarkbookForGroupsColumnData
> => {
  const methodConfig = getMethodConfig({
    cellProps,
    baseColumn,
  });

  const config = methodConfig[method.method_type];
  const groupIds = subject.groups.map((group) => group.id);

  const columnData = {
    data: { assessmentId: assessment.id, subject, method },
    type: 'assessment' as const,
    field: baseColumn.field,
    computedWidth: baseColumn.width,
  };

  return {
    field: baseColumn.field,
    sortable: baseColumn.sortable,
    disableColumnMenu: baseColumn.disableColumnMenu,
    width: baseColumn.width,
    editable: !isDisabled && baseColumn.editable,
    data: columnData.data,
    renderHeader: baseColumn.renderHeader,
    cellClassName: ({ row }: { row: MarkbookForGroupsRow }) => {
      return row.type === MarkbookForGroupsRowType.Student
        ? cellClassNameConfig.assessment[row.type][method.method_type]
        : cellClassNameConfig.assessment[row.type];
    },
    align: config.align as 'left' | 'center' | 'right',
    renderCell: (params: CellParams<MarkbookForGroupsRow, AssessmentCellState>) => {
      const { row, field } = params;

      switch (row.type) {
        case MarkbookForGroupsRowType.Student:
          const { student, assessmentEntries, categoryId } = row.data;

          if (isSorting) return null;

          const entriesForColumn = method.id
            ? getMarkbookForGroupsAssessmentEntries({
                assessmentId: assessment.id,
                groupIds,
                methodId: method.id,
                assessmentEntries,
              })
            : [];

          const isEmpty = entriesForColumn.length === 0;
          if (isEmpty) {
            return <AssessmentMarkbookEmptyCell />;
          }

          const isMultipleGroupsEntriesCell = hasMultipleGroupsEntriesInCell(entriesForColumn);
          if (isMultipleGroupsEntriesCell) {
            return (
              <MarkbookMultipleGroupsEntriesCell
                entries={sortByGroupName(entriesForColumn)}
                linkTo={{
                  pathname: `/students/${student.relation_id}`,
                  hash: `#${ProfileModalMode.Assessments}`,
                }}
              />
            );
          }

          const cellId = `${row.id}_${field}`;

          const { entries, groupId } =
            getAssessmentEntriesDataFromMarkbookEntries(entriesForColumn);

          return (
            <Stack
              sx={{ backgroundColor: isDisabled ? 'background.default' : undefined }}
              onClick={!isDisabled ? onCellClickOverride : undefined}
              {...config.stackProps}
            >
              {renderMethodCell({
                method,
                studentRelationId: student.relation_id,
                entries,
                groupId,
                subjectId: subject.id,
                isDisabled,
                assessmentId: assessment.id,
                canEdit:
                  !isDisabled &&
                  permissionChecker.isCellEditable({
                    row,
                    column: columnData,
                  }),
                scoreUpdateHandler,
                gradeUpdateHandler,
                options: list?.options ?? [],
                ...config.viewProps,
                params,
                categoryId,
                renderFallback: () =>
                  renderEntryCell({
                    student,
                    assessmentEntries: entriesForColumn,
                    assessmentId: assessment.id,
                    method,
                    cellId,
                    categoryId,
                    ...config.viewProps,
                  }),
              })}
            </Stack>
          );

        case MarkbookForGroupsRowType.Category:
          const sortBy = getMethodSortBy(method.method_type);
          const isSorted =
            sort?.sortBy === sortBy &&
            isGetMarkbookSortWithParams(sort) &&
            sort.assessmentId === assessment.id &&
            sort.subjectId === subject.id;
          const handleChangeSort = (sortBy: GetMarkbookSortBy) =>
            onChangeSort({ sortBy, assessmentId: assessment.id, subjectId: subject.id });

          if (isDisabled)
            return <AssessmentMarkbookDisabledMethodTypeCell onToggleCompare={onToggleCompare} />;

          return (
            <MarkbookMethodTypeCell
              type={method.method_type}
              onChangeSort={handleChangeSort}
              sortBy={sortBy}
              isSorted={isSorted}
              sortDirection={sort?.direction}
              justifyContent="center"
              isCompared={isCompared}
              onToggleCompare={onToggleCompare}
            />
          );

        case MarkbookForGroupsRowType.LoadMore:
        case MarkbookForGroupsRowType.LoadingNextPage:
          return null;
      }
    },
    renderEditCell: (params: CellParams<MarkbookForGroupsRow, AssessmentCellState>) => {
      const { row, field } = params;
      if (!isMarkbookForGroupsStudentRow(row)) return null;

      const { student, assessmentEntries, categoryId } = row.data;
      const editStackProps =
        method.method_type === AssessmentMethodType.Comment
          ? { minWidth: baseColumn.width, height: '100%' }
          : config.stackProps;

      const cellId = `${row.id}_${field}`;

      const entriesForColumn = method.id
        ? getMarkbookForGroupsAssessmentEntries({
            assessmentId: assessment.id,
            groupIds,
            methodId: method.id,
            assessmentEntries,
          })
        : [];

      const { entries, groupId } = getAssessmentEntriesDataFromMarkbookEntries(entriesForColumn);
      return (
        <Stack {...editStackProps}>
          {renderMethodCell({
            method,
            studentRelationId: student.relation_id,
            entries,
            subjectId: subject.id,
            groupId,
            assessmentId: assessment.id,
            canEdit:
              !isDisabled &&
              permissionChecker.isCellEditable({
                row,
                column: columnData,
              }),
            scoreUpdateHandler,
            gradeUpdateHandler,
            options: list?.options ?? [],
            ...config.editProps,
            params,
            categoryId,
            renderFallback: () =>
              renderEntryCell({
                student,
                assessmentEntries: entriesForColumn,
                assessmentId: assessment.id,
                method,
                cellId,
                categoryId,
                ...config.editProps,
              }),
          })}
        </Stack>
      );
    },
  };
};

type CreateMarkbookForGroupsStudentColumnParams = {
  styling?: ReturnType<typeof useMarkbookForGroupsStyling>;
  fetchNextPage: (id: string) => void;
  isFetchingNextPage: (id: string) => boolean;
  onChangeSort: (params: HandleChangeSortParams) => void;
  sort?: GetMarkbookSort;
  isSorting: boolean;
  categoryCellRenderer?: (row: MarkbookForGroupsCategoryRow) => React.ReactNode;
};

export const createMarkbookForGroupsStudentColumn = ({
  styling,
  fetchNextPage,
  isFetchingNextPage,
  onChangeSort,
  sort,
  isSorting,
  categoryCellRenderer,
}: CreateMarkbookForGroupsStudentColumnParams): ColumnDef<
  MarkbookForGroupsRow,
  AssessmentCellState,
  undefined
> => ({
  field: STUDENT_COL_FIELD_KEY,
  sortable: false,
  disableColumnMenu: true,
  flex: 1,
  minWidth: styling?.studentNameCellWidth,
  cellClassName: ({ row }: { row: MarkbookForGroupsRow }) => cellClassNameConfig.students[row.type],
  renderHeader: () => null,
  renderCell: (params: CellParams<MarkbookForGroupsRow, AssessmentCellState>) => {
    const { row } = params;

    switch (row.type) {
      case MarkbookForGroupsRowType.Student:
        if (isSorting) {
          return <MarkbookStudentNameSkeletonCell sx={{ p: 1 }} />;
        }

        const { student } = row.data;
        return (
          <MarkbookStudentNameCell
            student={student}
            linkProps={{ target: '_blank' }}
            cellProps={{ pl: 1, sx: styling?.studentCellSx }}
          />
        );

      case MarkbookForGroupsRowType.Category:
        const { category, totalStudents } = row.data;
        const sortBy = GetMarkbookSortBy.LastName;
        const isSorted = sort?.sortBy === sortBy;

        if (categoryCellRenderer) {
          return categoryCellRenderer(row);
        }

        return (
          <MarkbookCategoryNameCell
            category={category}
            totalStudents={totalStudents}
            onChangeSort={(sortBy) => onChangeSort({ sortBy })}
            sortDirection={sort?.direction}
            sortBy={sortBy}
            isSorted={isSorted}
            px={1}
          />
        );

      case MarkbookForGroupsRowType.LoadMore:
        const { categoryId } = row.data;
        return (
          <MarkbookLoadMoreCell
            alignItems="flex-start"
            height={ASSESSMENT_ROW_HEIGHT}
            onClick={() => fetchNextPage(categoryId)}
            isLoading={isFetchingNextPage(categoryId)}
            disabled={isSorting}
          />
        );

      case MarkbookForGroupsRowType.LoadingNextPage:
        return <MarkbookStudentNameSkeletonCell sx={{ p: 1 }} />;
    }
  },
});

type MethodConfig = {
  cellProps: ReturnType<typeof createCellPropsFactory>;
  baseColumn: BaseColumnData;
};

const getMethodConfig = ({ cellProps, baseColumn }: MethodConfig) => {
  return {
    [AssessmentMethodType.Score]: {
      align: 'center',
      stackProps: { minWidth: '100%', justifyContent: 'center', alignItems: 'center' },
      viewProps: {
        scoreControllerProps: {
          ...cellProps.getViewProps('score'),
          width: baseColumn.width,
        },
      },
      editProps: {
        scoreControllerProps: { ...cellProps.getEditProps('score'), width: baseColumn.width },
      },
    },
    [AssessmentMethodType.Grade]: {
      align: 'left',
      stackProps: {
        justifyContent: 'center',
        alignItems: 'center',
        justifyItems: 'center',
      },
      viewProps: {
        gradeControllerProps: {
          ...cellProps.getViewProps('grade'),
          dropdownStyleProps: {
            marginLeft: '-2px',
            marginTop: '-1px',
          },
          width: baseColumn.width + 1,
          height: baseColumn.height - 1,
          anchorOffset: 1,
        },
      },
      editProps: {
        gradeControllerProps: {
          ...cellProps.getEditProps('grade'),
          dropdownStyleProps: {
            marginLeft: '-2px',
            marginTop: '-1px',
          },
          width: baseColumn.width + 1,
          height: baseColumn.height,
          anchorOffset: 1,
        },
      },
    },
    [AssessmentMethodType.Comment]: {
      align: 'center',
      stackProps: { minWidth: '100%', height: '100%' },
      viewProps: { commentControllerProps: cellProps.getViewProps('comment') },
      editProps: { commentControllerProps: cellProps.getEditProps('comment') },
    },
  };
};

const cellClassNameConfig = {
  [MarkbookColumnType.Students]: {
    [MarkbookForGroupsRowType.Student]: ASSESSMENT_CSS_CLASSES.studentNameCell,
    [MarkbookForGroupsRowType.Category]: ASSESSMENT_CSS_CLASSES.categoryNameCell,
    [MarkbookForGroupsRowType.LoadMore]: ASSESSMENT_CSS_CLASSES.loadMoreCell,
    [MarkbookForGroupsRowType.LoadingNextPage]: ASSESSMENT_CSS_CLASSES.loadingNextPageCell,
  },
  [MarkbookColumnType.Assessment]: {
    [MarkbookForGroupsRowType.Student]: {
      [AssessmentMethodType.Score]: ASSESSMENT_CSS_CLASSES.scoreCell,
      [AssessmentMethodType.Grade]: ASSESSMENT_CSS_CLASSES.gradeCell,
      [AssessmentMethodType.Comment]: ASSESSMENT_CSS_CLASSES.commentCell,
    },
    [MarkbookForGroupsRowType.Category]: ASSESSMENT_CSS_CLASSES.methodTypeCell,
    [MarkbookForGroupsRowType.LoadMore]: ASSESSMENT_CSS_CLASSES.loadMoreCell,
    [MarkbookForGroupsRowType.LoadingNextPage]: ASSESSMENT_CSS_CLASSES.loadingNextPageCell,
  },
  [MarkbookColumnType.Average]: {
    [MarkbookForGroupsRowType.Student]: ASSESSMENT_CSS_CLASSES.averageCell,
    [MarkbookForGroupsRowType.Category]: ASSESSMENT_CSS_CLASSES.averageCategoryCell,
    [MarkbookForGroupsRowType.LoadMore]: ASSESSMENT_CSS_CLASSES.loadMoreCell,
    [MarkbookForGroupsRowType.LoadingNextPage]: ASSESSMENT_CSS_CLASSES.loadingNextPageCell,
  },
};
